import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { ReactRenderer, type Editor } from '@tiptap/react';
import type {
  SuggestionKeyDownProps,
  SuggestionOptions,
  SuggestionProps,
} from '@tiptap/suggestion';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';

export interface MenuItem {
  id: string;
  label: string;
  hint?: string;
  icon?: ReactNode;
  run: () => void;
}

export interface MenuHandle {
  onKeyDown(event: KeyboardEvent): boolean;
}

/** Keyboard-navigable list used by every suggestion popup. */
export const SuggestionMenu = forwardRef<MenuHandle, { items: MenuItem[]; empty: string }>(
  function SuggestionMenu({ items, empty }, ref) {
    const [index, setIndex] = useState(0);
    useEffect(() => setIndex(0), [items]);
    useImperativeHandle(ref, () => ({
      onKeyDown(event) {
        if (items.length === 0) return false;
        if (event.key === 'ArrowDown') {
          setIndex((i) => (i + 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowUp') {
          setIndex((i) => (i - 1 + items.length) % items.length);
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          items[index]?.run();
          return true;
        }
        return false;
      },
    }));
    return (
      <div className="nx-menu" role="listbox">
        {items.length === 0 ? (
          <div className="nx-menu-empty">{empty}</div>
        ) : (
          items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={i === index}
              className="nx-menu-item"
              onMouseEnter={() => setIndex(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                item.run();
              }}
              ref={(el) => {
                if (i === index) el?.scrollIntoView({ block: 'nearest' });
              }}
            >
              {item.icon && <span className="nx-menu-icon">{item.icon}</span>}
              <span className="nx-menu-label">{item.label}</span>
              {item.hint && <span className="nx-menu-hint">{item.hint}</span>}
            </button>
          ))
        )}
      </div>
    );
  },
);

/**
 * Keep the popup next to the caret: below it, or above when there is no room,
 * shrunk to the space there is. Follows scrolling, resizing and the menu's own
 * size (it grows after the first render), until the returned cleanup runs.
 */
function follow(el: HTMLElement, rect: () => DOMRect | null | undefined, context: Element) {
  let last = new DOMRect();
  const virtual = {
    getBoundingClientRect: () => (last = rect() ?? last),
    contextElement: context,
  };
  const update = () =>
    void computePosition(virtual, el, {
      placement: 'bottom-start',
      strategy: 'fixed',
      middleware: [
        offset(6),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        size({
          padding: 8,
          apply({ availableHeight }) {
            const menu = el.firstElementChild as HTMLElement | null;
            if (menu) menu.style.maxHeight = `${Math.max(120, Math.min(320, availableHeight))}px`;
          },
        }),
      ],
    }).then(({ x, y }) => Object.assign(el.style, { left: `${x}px`, top: `${y}px` }));
  return autoUpdate(virtual, el, update, { elementResize: true, animationFrame: false });
}

/**
 * Suggestion `render` implementation: mounts `Component` in a fixed-position
 * layer next to the caret and forwards keys to it.
 */
export function popupRenderer<P extends object>(
  Component: ComponentType<P & { ref?: React.Ref<MenuHandle> }>,
  toProps: (props: SuggestionProps) => P,
): NonNullable<SuggestionOptions['render']> {
  return () => {
    let renderer: ReactRenderer<MenuHandle, P> | null = null;
    let layer: HTMLDivElement | null = null;
    let stop: (() => void) | null = null;
    // The latest caret position (the suggestion's query grows as you type).
    let clientRect: (() => DOMRect | null) | null | undefined = null;
    const close = () => {
      stop?.();
      stop = null;
      layer?.remove();
    };
    return {
      onStart(props) {
        renderer = new ReactRenderer(Component as never, {
          props: toProps(props),
          editor: props.editor as Editor,
        });
        layer = document.createElement('div');
        layer.className = 'nx-popup';
        layer.appendChild(renderer.element);
        document.body.appendChild(layer);
        clientRect = props.clientRect;
        stop = follow(layer, () => clientRect?.(), props.editor.view.dom);
      },
      onUpdate(props) {
        renderer?.updateProps(toProps(props));
        clientRect = props.clientRect;
      },
      onKeyDown({ event }: SuggestionKeyDownProps) {
        if (event.key === 'Escape') {
          close();
          return true;
        }
        return renderer?.ref?.onKeyDown(event) ?? false;
      },
      onExit() {
        close();
        renderer?.destroy();
        layer = null;
        renderer = null;
      },
    };
  };
}
